/**
 * Tipsy Design System - Color Tokens
 *
 * Color palette for the Tipsy Super App.
 * All colors should be referenced through this file.
 */

export const palette = {
  // Primary brand colors
  primary: {
    50: '#E8F5E9',
    100: '#C8E6C9',
    200: '#A5D6A7',
    300: '#81C784',
    400: '#66BB6A',
    500: '#4CAF50', // Main primary
    600: '#43A047',
    700: '#388E3C',
    800: '#2E7D32',
    900: '#1B5E20',
  },

  // Secondary accent colors
  secondary: {
    50: '#FFF3E0',
    100: '#FFE0B2',
    200: '#FFCC80',
    300: '#FFB74D',
    400: '#FFA726',
    500: '#FF9800', // Main secondary
    600: '#FB8C00',
    700: '#F57C00',
    800: '#EF6C00',
    900: '#E65100',
  },

  // Neutral/Gray scale
  neutral: {
    0: '#FFFFFF',
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
    1000: '#000000',
  },

  // Semantic colors
  success: {
    light: '#81C784',
    main: '#4CAF50',
    dark: '#388E3C',
  },

  warning: {
    light: '#FFB74D',
    main: '#FF9800',
    dark: '#F57C00',
  },

  error: {
    light: '#E57373',
    main: '#F44336',
    dark: '#D32F2F',
  },

  info: {
    light: '#64B5F6',
    main: '#2196F3',
    dark: '#1976D2',
  },
} as const;

export const colors = {
  // Background colors
  background: {
    primary: palette.neutral[0], // White/transparent
    secondary: palette.neutral[50],
    tertiary: palette.neutral[100],
    inverse: palette.neutral[900],
    brand: '#FFDD00', // Tipsy Yellow - use for accents
  },

  // Surface colors (cards, modals, etc.)
  surface: {
    primary: palette.neutral[0],
    secondary: palette.neutral[50],
    elevated: palette.neutral[0],
  },

  // Text colors
  text: {
    primary: '#000000', // Black
    secondary: '#333333', // Dark gray
    tertiary: '#666666', // Medium gray
    disabled: palette.neutral[400],
    inverse: '#FFDD00', // Yellow for dark backgrounds
    link: '#000000', // Black links
  },

  // Border colors
  border: {
    light: palette.neutral[200],
    medium: palette.neutral[300],
    dark: palette.neutral[400],
    focus: palette.primary[500],
    error: palette.error.main,
  },

  // Interactive element colors
  interactive: {
    primary: '#000000', // Black buttons
    primaryHover: '#333333',
    primaryPressed: '#1A1A1A',
    primaryDisabled: '#999999',

    secondary: '#FFDD00', // Yellow secondary
    secondaryHover: '#E6C700',
    secondaryPressed: '#CCB100',

    destructive: palette.error.main,
    destructiveHover: palette.error.dark,
  },

  // Status colors
  status: {
    success: palette.success.main,
    successBackground: '#E8F5E9',
    warning: palette.warning.main,
    warningBackground: '#FFF3E0',
    error: palette.error.main,
    errorBackground: '#FFEBEE',
    info: palette.info.main,
    infoBackground: '#E3F2FD',
  },
} as const;

export type Palette = typeof palette;
export type Colors = typeof colors;
